import React, { useState, useRef, useEffect } from "react";
import { Button, message, Card, Typography } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import uploadService from "../../services/uploadService";
import * as tus from "tus-js-client";

const { Text } = Typography;

// Custom Progress Bar Component
const CustomProgressBar = ({
  progress,
  isComplete,
  color,
  uploadedBytes,
  totalBytes,
}) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div
      style={{
        marginBottom: "12px",
        padding: "12px",
        background: "#fafafa",
        borderRadius: "8px",
        border: "1px solid #f0f0f0",
      }}
    >
      {/* Progress Bar */}
      <div
        style={{
          width: "100%",
          height: "8px",
          backgroundColor: "#e8e8e8",
          borderRadius: "4px",
          overflow: "hidden",
          marginBottom: "8px",
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            backgroundColor: isComplete ? "#52c41a" : color,
            borderRadius: "4px",
            transition: "width 0.3s ease",
            position: "relative",
          }}
        >
          {/* Animated shine effect */}
          {!isComplete && progress > 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                animation: "shine 1.5s infinite",
              }}
            />
          )}
        </div>
      </div>

      {/* Progress Info */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "4px",
        }}
      >
        <Text
          strong
          style={{ fontSize: "14px", color: isComplete ? "#52c41a" : color }}
        >
          {progress}%
        </Text>

        {uploadedBytes && totalBytes && (
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {formatFileSize(uploadedBytes)} / {formatFileSize(totalBytes)}
          </Text>
        )}
      </div>
    </div>
  );
};

const CustomUploader = ({
  fileType = "trailer", // 'trailer' or 'video'
  language,
  movieName,
  onUploadComplete,
  onRemove,
  existingFileName = null,
  type, // movie, series, episode, season
  seasonNo,
  episodeNo,
}) => {
  const [fileName, setFileName] = useState(existingFileName);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const fileInputRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const generateTitle = () => {
    if (!movieName || !language) return "";
    if (type === "movie" || type === "series") {
      return `${movieName}_${fileType}_${language}_${Date.now()}`
        .replace(/\s+/g, "_")
        .toLowerCase();
    }
    if (type === "season") {
      if (!seasonNo || !language) return "";
      return `${movieName}_${fileType}_S${seasonNo}_${language}_${Date.now()}`
        .replace(/\s+/g, "_")
        .toLowerCase();
    }
    if (type === "episode") {
      if (!seasonNo || !episodeNo || !language) return "";
      return `${movieName}_${fileType}_S${seasonNo}_E${episodeNo}_${language}_${Date.now()}`
        .replace(/\s+/g, "_")
        .toLowerCase();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "video/mp4", 
      "video/x-matroska", 
      "video/mkv",
      "video/avi",
      "video/mov",
      "video/wmv",
      "video/flv",
      "video/webm",
      "video/3gp",
      "video/quicktime"
    ];
    if (!allowedTypes.includes(file.type)) {
      message.error("Please select a valid video file (MP4, MKV, AVI, MOV, WMV, FLV, WebM, 3GP)");
      return;
    }

    try {
      //generating token
      setStatus("Generating token...");
      setLoading(true);

      const title = generateTitle();
      setFileName(title);

      const payload = {
        title: title,
        video_type: fileType,
      };

      if (payload.title) {
        const res = await uploadService.generateToken(payload);

        if (res.data.success) {
          const { signature, token, libraryId, collectionId, bunnyGuid, videoId, expire } = res.data;

          setStatus("Token generated, starting upload...");

          const upload = new tus.Upload(file, {
            endpoint: "https://video.bunnycdn.com/tusupload", // Bunny TUS endpoint
            retryDelays: [0, 3000, 5000, 10000, 20000],
            chunkSize: 5242880,
            metadata: {
              title: title,
              filetype: file.type,
              collection: libraryId,
            },
            onBeforeRequest: (req) => {
              req.setHeader("AuthorizationSignature", String(signature));
              req.setHeader("AuthorizationExpire", String(expire));
              req.setHeader("LibraryId", String(libraryId));
              req.setHeader("VideoId", String(bunnyGuid));
              req.setHeader("CollectionId", String(collectionId));
            },
            onError: (error) => {
              console.error("Upload error:", error);
              setStatus(`Upload failed: ${error.message}`);
              setLoading(false);
            },
            onProgress: (bytesUploaded, bytesTotal) => {
              const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
              setProgress(percentage);
              setStatus(`Uploading... ${percentage}%`);
              setUploadedBytes(bytesUploaded);
              setTotalBytes(bytesTotal);
            },
            onSuccess: () => {
              setStatus("Upload completed successfully!");
              setLoading(false);
              onUploadComplete &&
                onUploadComplete({
                  file_name: title,
                  progress: progress,
                  bunny_guid: bunnyGuid,
                  video_id: videoId,
                  signature: signature,
                  token: token,
                });
            },
          });

          upload.start();
        }
      } else {
        setStatus("Please enter movie title");
        setLoading(false);
      }
    } catch (error) {
      console.error("Upload error:", error);
      message.error(`Failed to upload ${fileType}: ${error.message}`);
    }
  };

  const handleRemove = () => {
    setProgress(0);
    setUploadedBytes(0);
    setTotalBytes(0);
    setStatus("");
    setLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onRemove && onRemove();
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getFileTypeColor = () => {
    switch (fileType) {
      case "trailer":
        return "#52c41a";
      case "video":
        return "#1890ff";
      default:
        return "#722ed1";
    }
  };

  const getFileTypeBackground = () => {
    switch (fileType) {
      case "trailer":
        return "#f6ffed";
      case "video":
        return "#e6f7ff";
      default:
        return "#f9f0ff";
    }
  };

  const getFileTypeBorder = () => {
    switch (fileType) {
      case "trailer":
        return "#b7eb8f";
      case "video":
        return "#91d5ff";
      default:
        return "#d3adf7";
    }
  };

  useEffect(() => {
    if (!movieName || !language) {
      setStatus("Please enter movie title and language");
      setLoading(false);
    } else {
      setStatus("");
      setLoading(false);
    }
  }, [movieName, language]);

  return (
    <>
      <style>
        {`
          @keyframes shine {
            0% { left: -100%; }
            100% { left: 100%; }
          }
        `}
      </style>
      <Card style={{ border: "1px solid #d9d9d9", marginBottom: "16px" }}>
        <div style={{ marginBottom: "16px" }}>
          <Text strong style={{ color: getFileTypeColor() }}>
            {fileType.charAt(0).toUpperCase() + fileType.slice(1)} Upload
          </Text>

          {fileName && (
            <div
              style={{
                marginTop: "8px",
                padding: "8px 12px",
                background: getFileTypeBackground(),
                border: `1px solid ${getFileTypeBorder()}`,
                borderRadius: "6px",
                color: getFileTypeColor(),
              }}
            >
              <Text strong>{fileName}</Text>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,.mp4,.mkv,.avi,.mov,.wmv,.flv,.webm,.3gp"
          onChange={handleFileSelect}
          style={{ display: "none" }}
          disabled={loading || !generateTitle()}
        />

        <Button
          icon={<UploadOutlined />}
          onClick={handleUploadClick}
          loading={loading}
          disabled={loading || !generateTitle()}
          size="large"
          style={{ width: "100%", marginBottom: "12px" }}
        >
          {loading
            ? "Uploading..."
            : `Choose ${
                fileType.charAt(0).toUpperCase() + fileType.slice(1)
              } File`}
        </Button>

        {/* Status Strip */}
        {(status || loading) && (
          <div
            style={{
              marginBottom: "12px",
              padding: "10px 12px",
              background: loading ? "#fffbe6" : "#f6ffed",
              border: `1px solid ${loading ? "#ffe58f" : "#b7eb8f"}`,
              color: loading ? "#ad8b00" : "#389e0d",
              borderRadius: 6,
            }}
          >
            <Text strong>{status || (loading ? "Uploading..." : "Ready")}</Text>
          </div>
        )}

        {progress > 0 && (
          <CustomProgressBar
            progress={progress}
            isComplete={progress === 100}
            color={getFileTypeColor()}
            uploadedBytes={uploadedBytes}
            totalBytes={totalBytes}
          />
        )}

        <div style={{ textAlign: "right" }}>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={handleRemove}
          >
            Remove {fileType.charAt(0).toUpperCase() + fileType.slice(1)}
          </Button>
        </div>

        <div style={{ marginTop: "8px" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Supported formats: MP4, MKV, AVI, MOV, WMV, FLV, WebM, 3GP
          </Text>
        </div>
      </Card>
    </>
  );
};

export default CustomUploader;
